import { createApi } from 'unsplash-js';

// Unsplash API configuration
// Get your free API key at: https://unsplash.com/oauth/applications
const unsplash = createApi({
  accessKey: import.meta.env.VITE_UNSPLASH_ACCESS_KEY || 'demo', // Demo key for development
});

export interface UnsplashImage {
  id: string;
  urls: {
    raw: string;
    full: string;
    regular: string;
    small: string;
    thumb: string;
  };
  alt_description: string | null;
  user: {
    name: string;
    username: string;
  };
  links: {
    html: string;
  };
}

/**
 * Search for photos on Unsplash based on a query
 * @param query - Search term (e.g., "Troy archaeological site Turkey")
 * @param perPage - Number of results (default: 3)
 */
export async function searchUnsplashPhotos(
  query: string,
  perPage: number = 3
): Promise<UnsplashImage[]> {
  try {
    const result = await unsplash.search.getPhotos({
      query,
      perPage,
      orientation: 'landscape',
    });

    if (result.errors) {
      console.error('Unsplash API error:', result.errors);
      return [];
    }

    return result.response?.results || [];
  } catch (error) {
    console.error('Error fetching Unsplash photos:', error);
    return [];
  }
}

/**
 * Get optimized image URL with specific dimensions
 * @param image - Unsplash image object
 * @param width - Desired width in pixels
 * @param height - Desired height in pixels
 */
export function getOptimizedImageUrl(
  image: UnsplashImage,
  width: number = 1200,
  height: number = 800
): string {
  return `${image.urls.raw}&w=${width}&h=${height}&fit=crop&q=80`;
}

/**
 * Generate Unsplash attribution HTML
 * @param image - Unsplash image object
 */
export function getUnsplashAttribution(image: UnsplashImage): string {
  return `Photo by <a href="${image.links.html}?utm_source=theodysseymap&utm_medium=referral" target="_blank" rel="noopener noreferrer">${image.user.name}</a> on <a href="https://unsplash.com?utm_source=theodysseymap&utm_medium=referral" target="_blank" rel="noopener noreferrer">Unsplash</a>`;
}

/**
 * Location-specific search queries for better results
 */
export const locationSearchQueries: Record<number, string> = {
  1: 'Troy archaeological site Turkey ancient ruins',
  2: 'Thrace Greece coast Maroneia ancient',
  3: 'Djerba Tunisia beach Mediterranean island',
  4: 'Mount Etna Sicily Aci Trezza Faraglioni',
  5: 'Stromboli volcano night Aeolian Islands',
  6: 'Bonifacio Corsica white cliffs harbor',
  7: 'Monte Circeo Italy coastline Sabaudia',
  8: 'Acheron River Greece Epirus ancient',
  9: 'Amalfi Coast Positano Li Galli islands',
  10: 'Scilla Calabria Italy Strait of Messina',
  11: 'Valley of Temples Sicily Agrigento Greek',
  12: 'Gozo Malta Ramla Bay Calypso Cave',
  13: 'Corfu Greece Paleokastritsa old town',
  14: 'Ithaca Greece harbor island Ionian',
  15: 'Pylos Greece Navarino bay fortress',
  16: 'Mystras Greece Byzantine ruins Sparta',
  17: 'Mount Olympus Greece peak summit',
};

/**
 * Get search query for a specific location
 */
export function getLocationSearchQuery(locationId: number): string {
  return locationSearchQueries[locationId] || 'ancient Greece Mediterranean';
}
